import React, { useState } from 'react';
import { injectIntl } from 'react-intl';
import { parseClasses, isColorBright, cleanFieldProps, i18nIntl } from '../../lib/ep.js';

import ButtonCollection from './ButtonCollection';
import ColorPicker from './ColorPicker';
import FloatingLayer from './FloatingLayer';
import { MainButton, SquaredButton } from './ActionButton';
import ActionLink from './ActionLink';
import Model from './Model';
import Tooltip from './Tooltip.js';
import FieldWrapper from './FieldWrapper.js';

const ColorPairsSelector = (props) => {
	const i18n = (m, c) => i18nIntl(m, props.intl, c);

	const default_value = (props.value) ? props.value : ((props.model != undefined && props.value) ? props.model[props.name] : '');
	const default_foreground = props?.defs.foreground ?? 'rgba(255, 255, 255, 1)';
	const default_background = props?.defs.background ?? 'rgba(0, 0, 0, 1)';
	const min_colors = props?.min_colors ?? 0;
	const max_colors = props?.max_colors ?? 3;
	const skip_textcolor = props?.skip_textcolor ?? false;

	const [ value_arr, setValueArr ] = useState(default_value.split("\n"));
	const [ display_layer, setDisplayLayer ] = useState(false);
	const [ is_edit, setIsEdit ] = useState(false);
	const [ color_pair, setColorPair ] = useState({ fore:default_foreground, back:default_background });

	const change = (value) => {
		if(props.change != undefined) props.change(props.name, value.join("\n"));
	};

	const changeColor = (k, v) => {
		setColorPair({ ...color_pair, [k]:v });
	};

	const openLayer = (edit) => {
		const _is_edit = (edit != undefined) ? parseInt(edit) : false;

		if(_is_edit !== false) {
			const actual_color = parseColorPair(value_arr[_is_edit]);
			setColorPair({ fore:actual_color[1], back:actual_color[0] });
		} else {
			setColorPair({ fore:default_foreground, back:default_background });
		}

		setIsEdit(_is_edit);
		setDisplayLayer(true);
	};

	const addColor = () => {
		const _value_arr = [ ...value_arr ];
		_value_arr.push(mountColorPair());
		setValueArr(_value_arr);

		change(_value_arr);
		setDisplayLayer(false);
	};

	const deleteColor = (color_id) => {
		const _value_arr = [ ...value_arr ].filter((c, i) => i != color_id);
		setValueArr(_value_arr);

		change(_value_arr);
		setDisplayLayer(false);
	};

	const editColor = (color_id) => {
		const _value_arr = [ ...value_arr ];
		_value_arr[color_id] = mountColorPair();
		setValueArr(_value_arr);

		change(_value_arr);
		setDisplayLayer(false);
	};

	const parseColorPair = (pair) => {
		if(skip_textcolor) return [ pair, isColorBright(pair) ];
		if(pair.indexOf("|") != -1) return pair.split("|");
		return pair.split(",");
	};

	const mountColorPair = () => {
		if(skip_textcolor) return color_pair.back;
		return `${color_pair.back}|${color_pair.fore}`;
	};

	const wrapper_props = cleanFieldProps(props);
	
	let classname = props?.classname ?? 'input textarea';
	classname = parseClasses(classname, props._class, 'colorpairs');

	const newcolor_disabled = (value_arr.length >= max_colors) ? true : false;

	return (
		<>
			<FieldWrapper type='colorpairs' {...wrapper_props}>
				<ul class="gui_color_swatches">
					{value_arr.map((color, i) => {
						const color_comp = parseColorPair(color);
						const background_color = (skip_textcolor) ? color : color_comp[0];
						const text_color = (skip_textcolor) ? (isColorBright(color) ? 'black' : 'white') : color_comp[1];

						return (
							<li key={i} style={{ backgroundColor:background_color, color:text_color }}>
								<ActionLink
									onClick={() => openLayer(i)}
								>ABC</ActionLink>

								<ButtonCollection _class={{ other:'related_actions' }}>
									<SquaredButton
										icon='knk-edit mainButton whiteIcon'
										onClick={() => openLayer(i)}
										aria-label="Edit color"
										_class={{ other:'gui_small_icon' }}
									/>

									{(value_arr.length > min_colors) && (
										<SquaredButton
											icon='knk-delete redButton whiteIcon'
											onClick={() => deleteColor(i)}
											aria-label="Delete color"
											_class={{ other:'gui_small_icon' }}
										/>
									)}
								</ButtonCollection>
							</li>
						)
					})}

					<li class="add_more_colors">
						<MainButton
							squared={true}
							icon='knk-add-item'
							disabled={newcolor_disabled}
							tooltip={<Tooltip
								with_icon={false}
								autotranslate={false}
								position="top"
							>{(newcolor_disabled) ? props.intl.formatMessage({
								id:"You can add a maximum of {total} colors.",
							}, {
								total:max_colors,
							}) : i18n("Add a new color")}</Tooltip>}
							_class={{ in_top:false }}
							onClick={() => openLayer()}
						/>
					</li>
				</ul>
			</FieldWrapper>

			{(display_layer) && (
				<FloatingLayer
					_class={{ size:'small' }}
					backButton={() => setDisplayLayer(false)}
				>
					<div>
						<Model
							model={color_pair}
							change={changeColor}
						>
							{(!skip_textcolor) && (
								<ColorPicker
									name="fore"
									color={color_pair.fore}
									_class={{ first:true, size:60 }}
								>Text color</ColorPicker>
							)}

							<ColorPicker
								name="back"
								color={color_pair.back}
								_class={{ first:(skip_textcolor) ? true : false, size:60 }}
							>Background color</ColorPicker>
						</Model>

						<ButtonCollection in_floating={true}>
							{(is_edit === false) ? (
								<MainButton
									onClick={addColor}
								>Add</MainButton>
							) : (
								<MainButton
									onClick={() => editColor(is_edit)}
								>Edit</MainButton>
							)}
						</ButtonCollection>
					</div>
				</FloatingLayer>
			)}
		</>
	);
};

export default injectIntl(ColorPairsSelector);